import { Phone, ArrowDownRight } from 'lucide-react';
import { lazy, Suspense } from 'react';

import { hero } from '../content';
import { siteConfig } from '../../../shared/config/site';
import { useDeferredMount } from '../../../shared/hooks/useDeferredMount';
import { useInView } from '../../../shared/hooks/useInView';
import { usePrefersReducedMotion } from '../../../shared/hooks/usePrefersReducedMotion';
import { scrollToSection } from '../../../shared/lib/scroll';
import { supportsWebGl } from '../../../shared/lib/webgl';

const HeroCanvas = lazy(async () => {
  const module = await import('../components/HeroCanvas');
  return { default: module.HeroCanvas };
});

export function HeroSection() {
  const prefersReducedMotion = usePrefersReducedMotion();
  const { ref, inView } = useInView('50px');
  const deferred = useDeferredMount(1800);
  const showCanvas = inView && deferred && !prefersReducedMotion && supportsWebGl();

  return (
    <section id="top" ref={ref} className="relative min-h-[100dvh] overflow-hidden bg-neutral-950 text-white">
      {showCanvas ? (
        <Suspense fallback={<div className="absolute inset-0 bg-neutral-950" aria-hidden="true" />}>
          <HeroCanvas buildProgress={0.78} />
        </Suspense>
      ) : (
        <div
          className="absolute inset-0 bg-[radial-gradient(ellipse_at_70%_35%,#324152_0%,#0a0d12_58%)]"
          aria-hidden="true"
        />
      )}
      {/* The vertical scrim was transparent through its middle, which is exactly where the
          headline sits on a phone, so copy landed on lit scaffold boards. */}
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-neutral-950 via-neutral-950/70 to-transparent" />
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-neutral-950 via-neutral-950/72 to-neutral-950/45 sm:via-transparent" />
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.07]"
        style={{
          backgroundImage:
            'linear-gradient(to right, #fff 1px, transparent 1px), linear-gradient(to bottom, #fff 1px, transparent 1px)',
          backgroundSize: '48px 48px',
        }}
        aria-hidden="true"
      />
      <div
        className="pointer-events-none absolute -left-24 top-1/3 h-64 w-64 rounded-full bg-primary-400/15 blur-3xl"
        aria-hidden="true"
      />

      <div className="relative z-10 mx-auto flex min-h-[100dvh] max-w-[var(--container-max)] flex-col justify-center px-[var(--section-padding-x)] pb-28 pt-8 sm:pb-24 lg:pb-24 lg:pt-12">
        <div className="max-w-2xl">
          <p className="font-display text-[clamp(2.75rem,11vw,7rem)] font-semibold uppercase leading-[0.84] tracking-tight text-primary-400">
            {hero.brandLine}
          </p>
          <p className="mt-1 font-display text-[clamp(1.2rem,3.2vw,2.15rem)] font-medium uppercase tracking-wide text-white/88">
            {hero.brandSub}
          </p>
          <h1 className="mt-5 max-w-xl text-[clamp(1.25rem,3vw,1.95rem)] font-semibold leading-snug text-white sm:mt-7">
            {hero.headline}
          </h1>
          <p className="mt-3 max-w-md text-base leading-relaxed text-white/75 sm:mt-4 sm:text-lg">
            {hero.supportSentence}
          </p>
          <div className="mt-6 flex flex-col gap-3 sm:mt-8 sm:flex-row">
            <a href={siteConfig.phoneHref} className="btn-primary">
              <Phone className="h-4 w-4" aria-hidden="true" />
              {hero.primaryCta}
            </a>
            <button
              type="button"
              onClick={() => scrollToSection('fleet')}
              className="btn-secondary"
            >
              {hero.secondaryCta}
              <ArrowDownRight className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
